import { useCallback, useRef } from "preact/hooks";
import type { PageContext, PageVisit } from "@pankagent/shared";
import { streamMessage } from "../services/chatApi";

export interface UseChatStreamOptions {
  apiUrl: string;
  tenantId: string;
}

export interface StreamMessageParams {
  sessionId: string | null;
  message: string;
  pageContext: PageContext;
  recentPages?: PageVisit[];
}

export interface StreamCallbacks {
  onSessionInfo: (sessionId: string, messageId: string) => void;
  onTextChunk: (content: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

/**
 * Hook to handle streaming chat responses
 */
export function useChatStream(options: UseChatStreamOptions) {
  const { apiUrl, tenantId } = options;
  const isStreamingRef = useRef(false);

  const stream = useCallback(
    async (
      params: StreamMessageParams,
      callbacks: StreamCallbacks
    ): Promise<void> => {
      if (isStreamingRef.current) {
        console.warn("Stream already in progress");
        return;
      }

      isStreamingRef.current = true;

      try {
        await streamMessage(
          { apiUrl, tenantId },
          {
            sessionId: params.sessionId,
            message: params.message,
            pageContext: params.pageContext,
            recentPages: params.recentPages,
          },
          {
            onSessionInfo: callbacks.onSessionInfo,
            onTextChunk: callbacks.onTextChunk,
            onDone: () => {
              isStreamingRef.current = false;
              callbacks.onDone();
            },
            onError: (error) => {
              isStreamingRef.current = false;
              callbacks.onError(error);
            },
          }
        );
      } catch (error) {
        isStreamingRef.current = false;
        callbacks.onError(
          error instanceof Error ? error.message : "Stream failed"
        );
      }
    },
    [apiUrl, tenantId]
  );

  return { stream };
}
